import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, Modal, Switch, Space, Select, Tabs } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  getAllErrorGroups,
  createErrorGroup,
  getAllErrorCodes,
  createErrorCode,
  resetAll,
} from "../../../core/data/redux/errorSlice";
import { formatDate } from "@fullcalendar/core";
import { formatDateTime } from "../../../utils/dateUtils";

const routes = all_routes;
const { TextArea } = Input;

interface ErrorGroup {
  id: string;
  name: string;
  description?: string;
  active?: boolean;
  created_at?: string;
}

interface ErrorCode {
  id: string;
  code: string;
  message: string;
  group_id?: string;
  group?: any;
  http_status?: number;
  severity?: string;
  active?: boolean;
  created_at?: string;
}

const ErrorList: React.FC = () => {
  const dispatch = useDispatch();
  const errorState = useSelector((state: any) => state.errorState);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [listType, setListType] = useState<string>("groups");
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  const [showFormErrorToast, setShowFormErrorToast] = useState(false);
  const [showCreateSuccessToast, setShowCreateSuccessToast] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const groupsData = errorState?.groupsData || null;
  const codesData = errorState?.codesData || null;
  const createGroupData = errorState?.createGroupData || null;
  const createCodeData = errorState?.createCodeData || null;

  const fetchData = () => {
    if (listType === "groups") {
      dispatch(getAllErrorGroups({}));
    } else if (listType === "codes") {
      dispatch(getAllErrorCodes({}));
      if (!groupsData) {
        dispatch(getAllErrorGroups({}));
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [listType]);

  useEffect(() => {
    if (errorState.action === "create_group" && errorState.loading === "fulfilled") {
      setCreateSuccessMessage(createGroupData?.data?.message || createGroupData?.message || "Error group created successfully!");
      setShowCreateSuccessToast(true);
      setTimeout(() => {
        setShowCreateSuccessToast(false);
        dispatch(resetAll());
      }, 4000);
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      dispatch(getAllErrorGroups({}));
    }
  }, [errorState.action, errorState.loading, createGroupData]);

  useEffect(() => {
    if (errorState.action === "create_code" && errorState.loading === "fulfilled") {
      setCreateSuccessMessage(createCodeData?.data?.message || createCodeData?.message || "Error code created successfully!");
      setShowCreateSuccessToast(true);
      setTimeout(() => {
        setShowCreateSuccessToast(false);
        dispatch(resetAll());
      }, 4000);
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      dispatch(getAllErrorCodes({}));
    }
  }, [errorState.action, errorState.loading, createCodeData]);

  useEffect(() => {
    try {
      if (listType === "groups" && groupsData) {
        const apiResponse = groupsData?.data?.data;
        const data = apiResponse?.data || apiResponse || [];
        const total = apiResponse?.total || (Array.isArray(data) ? data.length : 0);
        setPagination((prev) => ({
          ...prev,
          total: total,
        }));
      } else if (listType === "codes" && codesData) {
        const apiResponse = codesData?.data?.data;
        const data = apiResponse?.data || apiResponse || [];
        const total = apiResponse?.total || (Array.isArray(data) ? data.length : 0);
        setPagination((prev) => ({
          ...prev,
          total: total,
        }));
      }
    } catch (error) {
      // Handle pagination error silently
    }
  }, [groupsData, codesData, listType]);

  const groupColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
    },
    {
      title: "Group Name",
      dataIndex: "name",
      render: (name: string) => (
        <div style={{ fontWeight: 500 }}>
          {name || "-"}
        </div>
      ),
      sorter: (a: ErrorGroup, b: ErrorGroup) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (description: string) => (
        <div style={{ maxWidth: "300px", wordWrap: "break-word" }}>
          {description || "-"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (_: any, record: ErrorGroup) => (
        <span
          className={`badge ${record.active ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {record.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      render: (date: string) => (
        <div style={{ fontSize: "12px" }}>
          {date
            ? formatDateTime(date)
            : "N/A"}
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: ErrorGroup) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  const codeColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
    },
    {
      title: "Error Code",
      dataIndex: "code",
      render: (code: string) => (
        <div style={{ fontWeight: 500, fontFamily: "monospace" }}>
          {code || "-"}
        </div>
      ),
      sorter: (a: ErrorCode, b: ErrorCode) => (a.code || "").localeCompare(b.code || ""),
    },
    {
      title: "Message",
      dataIndex: "message",
      render: (message: string) => (
        <div style={{ maxWidth: "250px", wordWrap: "break-word" }}>
          {message || "-"}
        </div>
      ),
    },
    {
      title: "Group",
      dataIndex: "group",
      render: (group: any, record: any) => (
        <span
          style={{
            backgroundColor: "#f0f0f0",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {group?.name || record?.group_name || record?.group_id || "-"}
        </span>
      ),
    },
    {
      title: "HTTP Status",
      dataIndex: "http_status",
      render: (httpStatus: number) => (
        <span
          style={{
            backgroundColor: "#e6f7ff",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#1890ff",
          }}
        >
          {httpStatus || "-"}
        </span>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      render: (severity: string) => {
        const getSeverityColor = (sev: string) => {
          switch (sev?.toLowerCase()) {
            case "error":
              return { bg: "#fff2f0", color: "#ff4d4f" };
            case "warning":
              return { bg: "#fff7e6", color: "#fa8c16" };
            case "info":
              return { bg: "#f6ffed", color: "#52c41a" };
            default:
              return { bg: "#f0f0f0", color: "#666666" };
          }
        };

        const colors = getSeverityColor(severity);
        return (
          <span
            style={{
              backgroundColor: colors.bg,
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              color: colors.color,
              textTransform: "capitalize",
            }}
          >
            {severity || "-"}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (_: any, record: ErrorCode) => (
        <span
          className={`badge ${record.active ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {record.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: ErrorCode) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      active: true
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: ErrorGroup | ErrorCode) => {
    setEditingId(record.id);

    const formValues = {
      ...record,
      httpStatus: (record as ErrorCode).http_status,
      groupId: (record as ErrorCode).group_id,
    };

    form.setFieldsValue(formValues);
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    const itemType = listType === "groups" ? "error group" : "error code";

    Modal.confirm({
      title: `Are you sure you want to delete this ${itemType}?`,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        setDeleteMessage(`Delete ${itemType} functionality will be implemented with API integration`);
        setShowDeleteToast(true);
        setTimeout(() => setShowDeleteToast(false), 3000);
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (listType === "groups") {
        if (editingId) {
          setUpdateMessage("Update error group functionality will be implemented with API integration");
          setShowUpdateToast(true);
          setTimeout(() => setShowUpdateToast(false), 3000);
          setIsModalVisible(false);
          form.resetFields();
          setEditingId(null);
        } else {
          const payload = {
            name: values.name,
            description: values.description,
            active: values.active
          };
          dispatch(createErrorGroup(payload));
        }
      } else if (listType === "codes") {
        if (editingId) {
          setUpdateMessage("Update error code functionality will be implemented with API integration");
          setShowUpdateToast(true);
          setTimeout(() => setShowUpdateToast(false), 3000);
          setIsModalVisible(false);
          form.resetFields();
          setEditingId(null);
        } else {
          const payload = {
            code: values.code,
            message: values.message,
            groupId: values.groupId,
            httpStatus: values.httpStatus ? parseInt(values.httpStatus, 10) : null,
            severity: values.severity,
            active: values.active
          };
          dispatch(createErrorCode(payload));
        }
      }
    } catch (error) {
      setFormErrorMessage("Please check all required fields and try again.");
      setShowFormErrorToast(true);
      setTimeout(() => setShowFormErrorToast(false), 3000);
    }
  };

  const handlePagination = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const getTableData = () => {
    if (listType === "groups") {
      let data = null;
      if (groupsData?.data?.data?.data && Array.isArray(groupsData.data.data.data)) {
        data = groupsData.data.data.data;
      } else if (groupsData?.data?.data && Array.isArray(groupsData.data.data)) {
        data = groupsData.data.data;
      } else if (groupsData?.result && Array.isArray(groupsData.result)) {
        data = groupsData.result;
      } else if (groupsData?.data && Array.isArray(groupsData.data)) {
        data = groupsData.data;
      } else {
        data = [];
      }

      return Array.isArray(data) ? data : [];
    } else if (listType === "codes") {
      let data = null;
      if (codesData?.data?.data?.data && Array.isArray(codesData.data.data.data)) {
        data = codesData.data.data.data;
      } else if (codesData?.data?.data && Array.isArray(codesData.data.data)) {
        data = codesData.data.data;
      } else if (codesData?.result && Array.isArray(codesData.result)) {
        data = codesData.result;
      } else if (codesData?.data && Array.isArray(codesData.data)) {
        data = codesData.data;
      } else {
        data = [];
      }

      return Array.isArray(data) ? data : [];
    }
    return [];
  };

  const tableData = getTableData();

  const getCurrentColumns = () => {
    return listType === "groups" ? groupColumns : codeColumns;
  };

  const getModalTitle = () => {
    if (listType === "groups") {
      return editingId ? "Edit Error Group" : "Add Error Group";
    } else {
      return editingId ? "Edit Error Code" : "Add Error Code";
    }
  };

  const getAddButtonText = () => {
    return listType === "groups" ? "Add Error Group" : "Add Error Code";
  };

  const getPageTitle = () => {
    return listType === "groups" ? "Error Groups" : "Error Codes";
  };

  return (
    <div className="page-wrapper">
      <Toasts
        show={errorState.loading === "rejected"}
        message={errorState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={showCreateSuccessToast}
        message={createSuccessMessage}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={showUpdateToast}
        message={updateMessage}
        title={"Info"}
        type={"warning"}
      />
      <Toasts
        show={showDeleteToast}
        message={deleteMessage}
        title={"Info"}
        type={"warning"}
      />
      <Toasts
        show={showFormErrorToast}
        message={formErrorMessage}
        title={"Validation Error"}
        type={"danger"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Error Management</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Settings</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Error List
                </li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2 d-flex align-items-center gap-2">
            <Button
              type="primary"
              onClick={handleAdd}
              icon={<PlusOutlined />}
              style={{
                backgroundColor: "#c0282c",
                border: "1px solid #c0282c",
                fontWeight: "500",
              }}
            >
              {getAddButtonText()}
            </Button>

            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <Tabs
              activeKey={listType}
              onChange={(key) => setListType(key)}
              items={[
                {
                  key: 'groups',
                  label: 'Error Groups',
                },
                {
                  key: 'codes',
                  label: 'Error Codes',
                },
              ]}
            />
          </div>
          <div className="card-body p-0 py-3">
            <Table
              columns={getCurrentColumns()}
              dataSource={tableData}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={errorState.loading === "pending"}
            />
          </div>
        </div>

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className={`ti ti-${editingId ? "edit" : "plus"}`} />
              {getModalTitle()}
            </div>
          }
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingId(null);
          }}
          confirmLoading={errorState.loading === "pending"}
          width={600}
          okText={editingId ? "Update" : "Create"}
          cancelText="Cancel"
        >
          <Form form={form} layout="vertical">
            {listType === "groups" ? (
              <React.Fragment>
                <Form.Item
                  name="name"
                  label="Group Name"
                  rules={[
                    { required: true, message: "Please input group name!" },
                    { min: 2, message: "Group name must be at least 2 characters!" },
                    { max: 100, message: "Group name cannot exceed 100 characters!" },
                  ]}
                >
                  <Input placeholder="Enter error group name" />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { max: 500, message: "Description cannot exceed 500 characters!" },
                  ]}
                >
                  <TextArea
                    placeholder="Enter group description (optional)"
                    rows={3}
                    showCount
                    maxLength={500}
                  />
                </Form.Item>
                <Form.Item
                  name="active"
                  label="Status"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Form.Item
                  name="code"
                  label="Error Code"
                  rules={[
                    { required: true, message: "Please input error code!" },
                    { min: 1, message: "Error code must be at least 1 character!" },
                    { max: 20, message: "Error code cannot exceed 20 characters!" },
                  ]}
                >
                  <Input placeholder="Enter error code (e.g., 404, E001)" />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="Error Message"
                  rules={[
                    { required: true, message: "Please input error message!" },
                    { min: 5, message: "Error message must be at least 5 characters!" },
                    { max: 200, message: "Error message cannot exceed 200 characters!" },
                  ]}
                >
                  <Input placeholder="Enter error message" />
                </Form.Item>
                <Form.Item
                  name="groupId"
                  label="Error Group"
                  rules={[
                    { required: true, message: "Please select an error group!" },
                  ]}
                >
                  <Select
                    placeholder="Select error group"
                    showSearch
                    optionFilterProp="children"
                    loading={errorState.loading === "pending" && errorState.action === "list_groups"}
                    style={{ width: "100%" }}
                  >
                    {(() => {
                      const groups = groupsData?.data?.data?.data ||
                        groupsData?.data?.data ||
                        groupsData?.result ||
                        groupsData?.data || [];
                      const groupsArray = Array.isArray(groups) ? groups : [];

                      return groupsArray.map((group: any) => (
                        <Select.Option key={group.id} value={group.id}>
                          {group.name}
                        </Select.Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="httpStatus"
                  label="HTTP Status"
                  rules={[
                    { pattern: /^\d+$/, message: "HTTP Status must be a valid number!" },
                    { max: 10, message: "HTTP Status cannot exceed 10 characters!" },
                  ]}
                >
                  <Input
                    type="number"
                    placeholder="Enter HTTP status (e.g., 400, 500)"
                    min={100}
                    max={599}
                  />
                </Form.Item>
                <Form.Item
                  name="severity"
                  label="Severity"
                  rules={[
                    { required: true, message: "Please select severity level!" },
                  ]}
                >
                  <Select placeholder="Select severity level">
                    <Select.Option value="error">Error</Select.Option>
                    <Select.Option value="warning">Warning</Select.Option>
                    <Select.Option value="info">Info</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="active"
                  label="Status"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </React.Fragment>
            )}
          </Form>
        </Modal>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">DMRC Admin Panel, All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default ErrorList;